import React, { FC, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { IonPage, useIonViewDidEnter } from '@ionic/react';
import { DateTime } from 'luxon';
import { ScreenDrumPickerFormFeature } from '@mindly/ui-components';
import { authStateSelector, signUpEmailSelector } from '../../../redux/auth/selector';
import { PublicRoutes } from '../../../routes/navigation';
import { updateSignUpData } from '../../../redux/auth/reducer';
import useToast from '../../../context/ToastContext/ToastContext';
import { useAnalytics } from '../../../shared';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';

const DoBPage: FC<RouteComponentProps> = ({ history }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('auth');
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { trackPredefinedEvent } = useAnalytics();

  const nameRef = useRef<HTMLInputElement | null>(null);

  const signUpEmail = useSelector(signUpEmailSelector);
  const authState = useSelector(authStateSelector);

  useIonViewDidEnter(() => {
    if (!signUpEmail) {
      const webToWebAuth = localStorage.getItem('webToWebAuth');

      if (webToWebAuth) {
        history.push(PublicRoutes.AUTH);
      } else {
        history.push(PublicRoutes.EMAIL_SIGN_IN);
      }
      return;
    }
  }, [signUpEmail, history]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'birthday',
    });
  });

  useIonViewDidEnter(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, [nameRef]);

  const handleDoBSubmit = useCallback(
    (value: Record<string, string>) => {
      if (!DateTime.fromFormat(`${value.day}.${value.month}.${value.year}`, 'dd.MM.yyyy').isValid) {
        showToast({ text: t('app:errors.date_invalid') });
        return;
      }
      const dobValue = `${value.day}.${value.month}.${value.year}`;
      trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
        screen_type: 'birthday',
        value: dobValue,
      });
      dispatch(updateSignUpData({ signUpDoB: dobValue }));
      history.push(PublicRoutes.COUNTRY_SIGN_UP);
    },
    [history, DateTime, dispatch, showToast, trackPredefinedEvent]
  );

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={PublicRoutes.LAST_NAME_SIGN_UP} />
      <ScreenDrumPickerFormFeature
        title={t('auth.date_of_birth.title')}
        subtitle={t('auth.date_of_birth.subtitle')}
        compareBy={'value'}
        selections={{
          day: Array.from({ length: 31 }, (_, i) => ({
            value: String(i + 1).padStart(2, '0'),
            label: String(i + 1).padStart(2, '0'),
          })),
          month: Array.from({ length: 12 }, (_, i) => ({
            value: DateTime.fromFormat((i + 1).toString(), 'M').toFormat('MM'),
            label: DateTime.local()
              .set({ month: i + 1 })
              .setLocale(language)
              .toFormat('LLLL'),
          })),
          year: Array.from({ length: DateTime.local().year - 18 - 1940 + 1 }, (_, i) => ({
            value: (1940 + i).toString(),
            label: (1940 + i).toString(),
          })),
        }}
        defaults={{
          day: authState?.signUpDoB ? DateTime.fromFormat(authState?.signUpDoB, 'dd.MM.yyyy').toFormat('dd') : '01',
          month: authState?.signUpDoB ? DateTime.fromFormat(authState?.signUpDoB, 'dd.MM.yyyy').toFormat('MM') : '01',
          year: authState?.signUpDoB
            ? DateTime.fromFormat(authState?.signUpDoB, 'dd.MM.yyyy').toFormat('yyyy')
            : (DateTime.local().year - 23).toString(),
        }}
        onChange={handleDoBSubmit}
        buttonText={t('auth.button.next')}
      />
    </IonPage>
  );
};

export default DoBPage;
